import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, TrendingUp, Loader2, Pencil } from "lucide-react";
import { apiClient } from "@/api/client";
import { formatApiError } from "@/utils/httpErrors";
import { VERTICALS } from "@/lib/verticals";

interface BenchmarkData {
  vertical: string;
  platform_multiple: number;
  market_floor: number;
  market_median: number;
  market_ceiling: number;
  relative_position: string;
  score: string;
  note: string;
  source_note: string;
  last_updated: string;
}

interface IndustryBenchmarkCardProps {
  dealId?: string;
}

export const IndustryBenchmarkCard: React.FC<IndustryBenchmarkCardProps> = ({ dealId }) => {
  const [data, setData] = useState<BenchmarkData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // DEBT-VERTICAL-WRITEONCE-1: this card was the clearest evidence of the
  // defect — it names the exact vertical driving the number shown, with no
  // way to fix it if intake left the default or guessed wrong. refreshKey
  // re-runs the load effect after a successful save, same as
  // Normalization.tsx's bankRefreshKey pattern elsewhere in this product.
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState(false);
  const [pendingVertical, setPendingVertical] = useState("");
  const [savingVertical, setSavingVertical] = useState(false);

  useEffect(() => {
    if (!dealId) {
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<BenchmarkData>(`/qoe/deals/${dealId}/industry-benchmark`);
        if (!cancelled) setData(res.data);
      } catch (err: any) {
        if (!cancelled) {
          if (err.response?.status === 404) {
            setError("No benchmark data available yet. Complete the executive summary first.");
          } else if (err.response?.status === 422) {
            setError(formatApiError(err));
          } else {
            setError("Failed to load benchmark data");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [dealId, refreshKey]);

  const startEditing = () => {
    setPendingVertical(data?.vertical || VERTICALS[VERTICALS.length - 1]);
    setEditing(true);
  };

  // Same preserve-on-omit / optimistic-save shape as Normalization.tsx's
  // changeCurrency — the other deal-identity field that saves outside its
  // own screen. On failure the card just re-shows the value it already had;
  // there is nothing local to roll back since this card never wrote `data`
  // ahead of the server confirming it.
  const saveVertical = async () => {
    if (!dealId || !pendingVertical) return;
    setSavingVertical(true);
    try {
      await apiClient.patch(`/qoe/deals/${dealId}`, { vertical: pendingVertical });
      setEditing(false);
      setRefreshKey((k) => k + 1);
      toast.success("Industry vertical updated.");
    } catch (err: any) {
      toast.error(
        err.response?.status === 422
          ? formatApiError(err)
          : "The industry vertical could not be saved. Please try again in a moment."
      );
    } finally {
      setSavingVertical(false);
    }
  };

  if (!dealId) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="ml-2">Loading benchmark...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-amber-50 border-l-4 border-amber-400">
        <CardContent className="p-4">
          <div className="flex gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{error || "No benchmark data available"}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const scoreColor = {
    PASS: "text-green-600",
    REVIEW: "text-amber-600",
    FLAG: "text-red-600",
  }[data.score] || "text-gray-600";

  const scoreBg = {
    PASS: "bg-green-50",
    REVIEW: "bg-amber-50",
    FLAG: "bg-red-50",
  }[data.score] || "bg-gray-50";

  // Visual range: 0–100% scale where floor is 25%, median is 50%, ceiling is 75%
  const rangeMin = Math.max(0, data.market_floor - (data.market_ceiling - data.market_floor) * 0.25);
  const rangeMax = data.market_ceiling + (data.market_ceiling - data.market_floor) * 0.25;
  const scale = (val: number) => Math.max(0, Math.min(100, ((val - rangeMin) / (rangeMax - rangeMin)) * 100));

  const floorPercent = scale(data.market_floor);
  const medianPercent = scale(data.market_median);
  const ceilingPercent = scale(data.market_ceiling);
  const platformPercent = scale(data.platform_multiple);

  return (
    <Card className={`${scoreBg} border-l-4 ${scoreColor.replace("text", "border")}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Valuation Benchmark
        </CardTitle>
        {editing ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Select value={pendingVertical} onValueChange={(v) => v && setPendingVertical(v)}>
              <SelectTrigger className="h-8 w-full max-w-xs bg-white sm:w-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VERTICALS.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={saveVertical}
              disabled={savingVertical}
              className="rounded bg-[var(--color-primary,#0d1f3c)] px-2.5 py-1 text-xs font-medium
                         text-white disabled:opacity-50"
            >
              {savingVertical ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={savingVertical}
              className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium
                         text-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <CardDescription className="flex flex-wrap items-center gap-2">
            {data.vertical}
            <button
              type="button"
              onClick={startEditing}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
            >
              <Pencil className="h-3 w-3" />
              Change
            </button>
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between text-sm font-medium">
            <span>Platform multiple:</span>
            <span className={scoreColor}>{data.platform_multiple.toFixed(1)}x SDE</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Market range:</span>
            <span className="text-gray-700">
              {data.market_floor.toFixed(1)}x — {data.market_ceiling.toFixed(1)}x
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Market median:</span>
            <span className="text-gray-700">{data.market_median.toFixed(1)}x</span>
          </div>
        </div>

        {/* Visual range bar */}
        <div className="space-y-2">
          <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
            {/* Market range background */}
            <div
              className="absolute h-full bg-blue-200"
              style={{
                left: `${floorPercent}%`,
                right: `${100 - ceilingPercent}%`,
              }}
            />
            {/* Markers */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-400"
              style={{ left: `${floorPercent}%` }}
              title={`Floor: ${data.market_floor.toFixed(1)}x`}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2 h-6 bg-blue-600"
              style={{ left: `${medianPercent}%`, marginLeft: "-4px" }}
              title={`Median: ${data.market_median.toFixed(1)}x`}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-400"
              style={{ left: `${ceilingPercent}%` }}
              title={`Ceiling: ${data.market_ceiling.toFixed(1)}x`}
            />
            {/* Platform marker */}
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-0.5 h-8 -translate-x-1/2 -translate-y-1/2 border-2 ${scoreColor.replace("text", "border")}`}
              style={{ left: `${platformPercent}%` }}
              title={`This deal: ${data.platform_multiple.toFixed(1)}x`}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>{data.market_floor.toFixed(1)}x</span>
            <span>{data.market_median.toFixed(1)}x</span>
            <span>{data.market_ceiling.toFixed(1)}x</span>
          </div>
        </div>

        {/* Status */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Position:</span>
            <span className={`text-sm font-semibold ${scoreColor}`}>
              {data.relative_position}
              {data.score === "PASS" && " ✅"}
              {data.score === "REVIEW" && " ⚠️"}
              {data.score === "FLAG" && " ⛔"}
            </span>
          </div>
        </div>

        {/* Note */}
        <div className="text-sm text-gray-700 p-3 bg-white rounded border border-gray-300">
          {data.note}
        </div>

        {/* Source */}
        <div className="text-xs text-gray-600 space-y-1">
          <p>
            <strong>Source:</strong> {data.source_note}
          </p>
          <p>
            <strong>Updated:</strong> {data.last_updated}
          </p>
        </div>

        {/* Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 flex gap-2">
          <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-800">
            These are illustrative benchmarks — not a valuation opinion. Obtain an independent business
            valuation for final pricing.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
